import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LifeBuoy, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminSupportTickets() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("open");
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [close, setClose] = useState(false);

  const load = () => api.get(`/admin/support/tickets${status ? `?status=${status}` : ""}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [status]);

  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/support/tickets/${selected.id}/reply`, { body: reply, close });
      toast.success(close ? "Ticket closed" : "Reply sent");
      setSelected(null); setReply(""); setClose(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reply failed");
    }
  };

  return (
    <div data-testid="admin-support">
      <div className="pf-chip pf-chip-mint"><LifeBuoy className="w-3 h-3" /> Support</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Support tickets</h2>

      <div className="flex gap-2 mt-6 mb-4">
        {[["open", "Open"], ["closed", "Closed"], ["", "All"]].map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${status === v ? "pf-btn-primary" : "pf-btn-glass"}`} data-testid={`ticket-filter-${v || "all"}`}>{l}</button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="pf-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
              <tr><th className="px-4 py-3">Subject</th><th className="px-4 py-3">From</th><th className="px-4 py-3">Status</th></tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} onClick={() => setSelected(t)} className={`border-t border-white/60 cursor-pointer hover:bg-white/40 ${selected?.id === t.id ? "bg-white/60" : ""}`} data-testid={`ticket-row-${t.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{t.subject}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{t.opener_email}</td>
                  <td className="px-4 py-3"><span className={`pf-chip ${t.status === "open" ? "pf-chip-lav" : "pf-chip-mint"}`}>{t.status}</span></td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={3} className="px-4 py-12 text-center text-slate-500">No tickets.</td></tr>}
            </tbody>
          </table>
        </div>

        <div>
          {selected ? (
            <div className="pf-card p-6" data-testid="ticket-detail">
              <div className="flex items-center justify-between mb-2">
                <div className="pf-chip">{selected.category}</div>
                <span className={`pf-chip ${selected.status === "open" ? "pf-chip-lav" : "pf-chip-mint"}`}>{selected.status}</span>
              </div>
              <h3 className="font-display text-2xl text-slate-900">{selected.subject}</h3>
              <div className="text-xs text-slate-500 mt-1">
                {selected.opener_name} · {selected.opener_email} · {new Date(selected.created_at).toLocaleString()}
              </div>
              <p className="text-sm text-slate-800 mt-4 whitespace-pre-wrap">{selected.body}</p>

              <div className="mt-6 space-y-3 max-h-64 overflow-y-auto">
                {(selected.messages || []).map((m) => (
                  <div key={m.id} className="bg-white/60 rounded-xl p-3 text-sm">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span className="font-semibold">{m.author_name} ({m.author_role})</span>
                      <span>{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    <div className="whitespace-pre-wrap">{m.body}</div>
                  </div>
                ))}
              </div>

              {selected.status === "open" && (
                <form onSubmit={send} className="mt-6 space-y-3">
                  <textarea required value={reply} onChange={(e) => setReply(e.target.value)} rows={3} className="pf-input w-full px-3 py-2 text-sm" placeholder="Type a reply…" data-testid="ticket-reply-input" />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={close} onChange={(e) => setClose(e.target.checked)} data-testid="ticket-close-toggle" /> Close after reply</label>
                    <button type="submit" className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="ticket-send">
                      {close ? <CheckCircle2 className="w-3 h-3" /> : <Send className="w-3 h-3" />} {close ? "Close" : "Send"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <div className="pf-card p-8 text-center text-slate-500 text-sm">Select a ticket to view + reply.</div>
          )}
        </div>
      </div>
    </div>
  );
}
